import {
  AfterContentInit,
  Component,
  ContentChildren,
  HostListener,
  Input,
  QueryList,
  forwardRef,
  isDevMode,
  OnDestroy
} from '@angular/core';
import { Subscription } from 'rxjs';
import { MktAccordionSectionComponent } from './mkt-accordion-section/mkt-accordion-section.component';
import { CdsAnnouncerService } from '@citi-gcg-173875/citi-common-ui-cds';


@Component({
  selector: 'mkt-accordion',
  templateUrl: './mkt-accordion.component.html',
  styleUrls: ['./mkt-accordion.component.scss'],
  standalone: false
})
export class MktAccordionComponent implements AfterContentInit, OnDestroy {

  /**
   * @property allSectionsOpen
   * @description Whether all sections are opened/expanded
   */
  allSectionsOpen: boolean = true;

  /**
   * @property accordionTitle
   * @description The title displayed at the top of the accordion and populates the <legend> tag pair
   * @return string
   */
  @Input() accordionTitle: string;

  /**
   * @property accordionTitle
   * @description The title displayed at the top of the accordion and populates the <h3> tag
   * @return string
   */
  @Input() sectionMainTitle: string;

  /**
   * @property hideToggle
   * @description hide/show expand and collapse link
   */
  @Input() hideToggle: boolean = true;

  /**
   * @property multi
   * @description whether mulitple section are permitted to be open at the same time
   */
  @Input() multi: boolean = true;

  /**
   * @property theme
   * @description Default theme is light. The value 'dark' can be passed to this input to use the dark theme.
   * @return string
   */
  @Input() theme: string;

  /**
   * @property collapseAllButtonText
   * @description Button text displayed when all items are expanded
   * @return string
   */
  @Input() collapseAllButtonText: string = 'Collapse All Sections';

  /**
   * @property expandAllButtonText
   * @description Button text displayed when all items are collapsed
   * @return string
   */
  @Input() expandAllButtonText: string = 'Expand All Sections';

  /**
   * @property expandAllButtonText
   * @description Button text displayed when all items are collapsed
   * @return string
   */
  @Input() closeOthersText: string = 'Other Sections Closed';

  /**
 * @property isRightAlignedArrow
 * @description To display the arrow at right side.
 * Default behavior is false - which means accordion will show the left aligned arrow.
 * @twoway false
 */
  @Input() isRightAlignedArrow: boolean;

  @ContentChildren(forwardRef(() => MktAccordionSectionComponent)) sections: QueryList<MktAccordionSectionComponent>;


  get noSectionsOpen(): boolean {
    return this.sections.find(section => section.isOpen) === undefined;
  }


  @HostListener('keydown.home', ['$event'])
  onKeyPressHome(event: KeyboardEvent) {
    event.preventDefault();
    this.sections.first.focusButton()
  }

  @HostListener('keydown.end', ['$event'])
  onKeyPressEnd(event: KeyboardEvent) {
    event.preventDefault();
    this.sections.last.focusButton()
  }

  private sectionClickSubscriptions: Subscription = new Subscription();

  //******************************    LIFECYCLE    *******

  constructor(private announcer: CdsAnnouncerService) { }

  ngAfterContentInit() {
    this.checkAllSections();
    this.sections.forEach((section, index) => (section.sectionIndex = index));
    this.listenForClicks();
  }

  ngOnDestroy() {
    this.sectionClickSubscriptions.unsubscribe();
  }

  listenForClicks(): void {
    this.sections.forEach(section => {
      let sectionSubscription = section.sectionClicked.subscribe(index => {
        if (!this.multi) this.setAllSection(index);
        this.handleSectionToggle(index);
        this.checkAllSections();
      });
      this.sectionClickSubscriptions.add(sectionSubscription)
    });
  }

  handleSectionToggle(index: number) {
    const targetSection = this.sections.find(
      section => section.sectionIndex === index
    );
    targetSection.isOpen = !targetSection.isOpen;
  }


  checkAllSections() {
    this.allSectionsOpen = this.noSectionsOpen
      ? false
      : !this.sections.some(section => !section.isOpen);
  }

  setAllSection(excludeIndex?: number): void {
    this.sections.forEach(section => {
      if (section.sectionIndex !== excludeIndex && section.isOpen) {
        section.isOpen = false;
        // prevents collision with "expand"/"collapse" announcements from the sections
        setTimeout(() => this.announcer.announce(this.closeOthersText), 101);
      }
    });
  }

  /**
   * @Method: toggleAllSections
   * @description Opens/closes all sections
   * @return Void
   */
  toggleAllSections(event?: MouseEvent): void {
    if (event) event.preventDefault();
    this.allSectionsOpen = !this.allSectionsOpen;
    this.sections.forEach(section => (section.isOpen = this.allSectionsOpen));
  }

  @Input() set childIds(value) {
    if (isDevMode) console.warn('"childIds" is a depreacted attribute on the accordion component and will be retired in Janurary. Please remove this attribute.')
  }
}
